import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { FaArrowRight, FaHistory, FaSearch, FaSpinner, FaTimes } from 'react-icons/fa'

import {
  clearRecent as forgetRecent,
  Highlight,
  Hit,
  Kind,
  KIND_INFO,
  KINDS,
  MIN_CHARS,
  pathFor,
  quickSearch,
  readRecent,
  rememberRecent,
  resultsPathFor,
} from './searchShared'

/**
 * Search in place from the header: results drop down as you type, grouped by
 * kind, and open straight from the list. Enter shows every match on the full
 * results page (/search), unless a row has been picked with the arrow keys.
 *
 * Uses the quick search (GET /search/quick), which returns just the newest few
 * jobs, quotes and enquiries that match rather than every match - all a
 * dropdown needs, and what keeps it fast enough to run as you type. "Show
 * more" asks for further matches of one kind. Answers are kept for the visit,
 * so going back over something already typed is instant.
 *
 */

const DEBOUNCE_MS = 200
const PER_GROUP = 4
const MORE_TAKE = 25
const CACHE_MS = 60_000

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

const AppSearch = () => {
  const navigate = useNavigate()

  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const [results, setResults] = useState<Hit[] | null>(null)
  const [more, setMore] = useState<Kind[]>([])
  const [searched, setSearched] = useState('')
  const [loadingMore, setLoadingMore] = useState<Kind | null>(null)

  // Answers already fetched this visit, by search text.
  const cache = useRef(new Map<string, { hits: Hit[]; more: Kind[]; at: number }>())
  // The row picked with the arrow keys, or -1 for none - when Enter shows
  // every result instead of opening one.
  const [active, setActive] = useState(-1)
  const [recent, setRecent] = useState<Hit[]>(readRecent)

  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const term = query.trim()

  // Search as the user types, after a short pause. Each new search cancels the
  // one before - in the browser and at the database - so a slow early reply
  // can never overwrite a later one. What's on screen stays until the new
  // answer arrives, rather than blanking on every keystroke.
  useEffect(() => {
    if (term.length < MIN_CHARS) {
      setResults(null)
      setLoading(false)
      setFailed(false)
      return
    }

    const key = term.toLowerCase()
    const cached = cache.current.get(key)
    if (cached && Date.now() - cached.at < CACHE_MS) {
      setResults(cached.hits)
      setMore(cached.more)
      setSearched(term)
      setFailed(false)
      setLoading(false)
      setActive(-1)
      return
    }

    const controller = new AbortController()
    setLoading(true)

    const timer = window.setTimeout(async () => {
      try {
        const found = await quickSearch(term, controller.signal, { take: PER_GROUP })
        cache.current.set(key, { ...found, at: Date.now() })
        setResults(found.hits)
        setMore(found.more)
        setSearched(term)
        setFailed(false)
        setActive(-1)
        setLoading(false)
      } catch (error) {
        if (axios.isCancel(error)) return
        setFailed(true)
        setResults(null)
        setLoading(false)
      }
    }, DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [term])

  /** Fetches further matches of one kind and adds them to its group. */
  const showMore = async (kind: Kind) => {
    if (!searched) return
    setLoadingMore(kind)
    try {
      const found = await quickSearch(searched, new AbortController().signal, { type: kind, take: MORE_TAKE })
      const merged = [...(results ?? []).filter((h) => h.kind !== kind), ...found.hits]
      const nextMore = [...more.filter((k) => k !== kind), ...found.more]
      setResults(merged)
      setMore(nextMore)
      cache.current.set(searched.toLowerCase(), { hits: merged, more: nextMore, at: Date.now() })
    } catch {
      // The first few are still showing; nothing more to do.
    } finally {
      setLoadingMore(null)
    }
  }

  // Ctrl/Cmd+K from anywhere puts you in the search.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        inputRef.current?.focus()
        inputRef.current?.select()
        setOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // A click outside closes the dropdown.
  useEffect(() => {
    if (!open) return
    const onDown = (e: globalThis.MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  // What the dropdown lists, in order: grouped results while searching,
  // otherwise what was opened recently.
  const groups = useMemo(() => {
    if (!results) return []
    return KINDS.map((k) => {
      const all = results.filter((r) => r.kind === k.kind)
      return { ...k, all, shown: all, hasMore: more.includes(k.kind) }
    }).filter((g) => g.all.length > 0)
  }, [results, more])

  const showingRecent = term.length < MIN_CHARS
  const flat: Hit[] = showingRecent ? recent : groups.flatMap((g) => g.shown)

  // Keep the highlighted row in view as the arrow keys move it.
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [active])

  const openHit = useCallback(
    (hit: Hit, newTab = false) => {
      rememberRecent(hit)
      setRecent(readRecent())
      const path = pathFor(hit)
      if (newTab) {
        window.open(path, '_blank', 'noopener')
        return
      }
      setOpen(false)
      setQuery('')
      inputRef.current?.blur()
      navigate(path)
    },
    [navigate]
  )

  /** Every match, on the full results page. */
  const showAll = useCallback(
    (newTab = false) => {
      const path = resultsPathFor(term)
      if (newTab) {
        window.open(path, '_blank', 'noopener')
        return
      }
      setOpen(false)
      inputRef.current?.blur()
      navigate(path)
    },
    [navigate, term]
  )

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setActive((i) => (flat.length ? (i + 1) % flat.length : -1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => (flat.length ? (i <= 0 ? flat.length - 1 : i - 1) : -1))
    } else if (e.key === 'Enter') {
      const hit = active >= 0 ? flat[active] : undefined
      if (hit) {
        e.preventDefault()
        openHit(hit, e.metaKey || e.ctrlKey)
      } else if (term.length >= MIN_CHARS) {
        e.preventDefault()
        showAll(e.metaKey || e.ctrlKey)
      }
    } else if (e.key === 'Escape') {
      if (query) setQuery('')
      else {
        setOpen(false)
        inputRef.current?.blur()
      }
    }
  }

  const onRowClick = (e: MouseEvent, hit: Hit) => {
    e.preventDefault()
    openHit(hit, e.metaKey || e.ctrlKey || e.button === 1)
  }

  const clearRecent = () => {
    forgetRecent()
    setRecent([])
  }

  let index = -1
  const row = (hit: Hit) => {
    index += 1
    const i = index
    const info = KIND_INFO[hit.kind]
    return (
      <a
        key={hit.key}
        href={pathFor(hit)}
        data-index={i}
        role='option'
        aria-selected={active === i}
        className={`psr-row${active === i ? ' psr-row--active' : ''}`}
        onClick={(e) => onRowClick(e, hit)}
      >
        <span className={`psr-row__icon psr-tone--${info.tone}`} aria-hidden='true'>
          {info.icon}
        </span>
        <span className='psr-row__body'>
          <span className='psr-row__top'>
            {hit.ref && (
              <span className='psr-row__ref'>
                <Highlight text={hit.ref} query={searched} />
              </span>
            )}
            <span className='psr-row__title'>
              <Highlight text={hit.title} query={searched} />
            </span>
          </span>
          {(hit.detail || hit.place) && (
            <span className='psr-row__sub'>
              <Highlight text={[hit.detail, hit.place].filter(Boolean).join(' · ')} query={searched} />
            </span>
          )}
        </span>
        <span className='psr-row__kind'>{hit.kind}</span>
      </a>
    )
  }

  const total = results?.length ?? 0

  return (
    <div ref={rootRef} className={`psr${open ? ' psr--open' : ''}`}>
      <div className='psr-box'>
        {loading ? (
          <FaSpinner className='psr-box__icon psr-spin' aria-hidden='true' />
        ) : (
          <FaSearch className='psr-box__icon' aria-hidden='true' />
        )}
        <input
          ref={inputRef}
          className='psr-box__input'
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
            setActive(-1)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder='Search jobs, quotes, enquiries, clients…'
          role='combobox'
          aria-expanded={open}
          aria-controls='psr-results'
          aria-autocomplete='list'
          aria-label='Search'
        />
        {query ? (
          <button
            type='button'
            className='psr-box__clear'
            onClick={() => {
              setQuery('')
              inputRef.current?.focus()
            }}
            aria-label='Clear search'
          >
            <FaTimes />
          </button>
        ) : (
          <kbd className='psr-box__kbd'>{isMac ? '⌘K' : 'Ctrl K'}</kbd>
        )}
      </div>

      {open && (
        <div className='psr-pop' id='psr-results' role='listbox' aria-label='Search results'>
          <div className='psr-pop__scroll' ref={listRef}>
            {showingRecent ? (
              recent.length > 0 ? (
                <div className='psr-group'>
                  <div className='psr-group__head'>
                    <span>
                      <FaHistory aria-hidden='true' /> Recently opened
                    </span>
                    <button type='button' className='psr-link' onClick={clearRecent}>
                      Clear
                    </button>
                  </div>
                  {recent.map(row)}
                </div>
              ) : (
                <p className='psr-hint'>
                  Type at least {MIN_CHARS} letters to search jobs, quotes and enquiries - by reference (J004498),
                  client, contact, address, postcode, phone or email.
                </p>
              )
            ) : failed ? (
              <p className='psr-hint'>Search isn't available right now. Try again in a moment.</p>
            ) : results === null ? (
              <p className='psr-hint'>Searching…</p>
            ) : total === 0 ? (
              <p className='psr-hint'>
                Nothing matches “<strong>{searched}</strong>”.
              </p>
            ) : (
              groups.map((g) => (
                <div key={g.kind} className='psr-group'>
                  <div className='psr-group__head'>
                    <span>
                      {g.plural}{' '}
                      <span className='psr-group__count'>
                        {g.all.length}
                        {g.hasMore ? '+' : ''}
                      </span>
                    </span>
                    {g.hasMore && (
                      <button
                        type='button'
                        className='psr-link'
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => showMore(g.kind)}
                        disabled={loadingMore === g.kind}
                      >
                        {loadingMore === g.kind ? 'Loading…' : 'Show more'}
                      </button>
                    )}
                  </div>
                  {g.shown.map(row)}
                </div>
              ))
            )}
          </div>

          {!showingRecent && !failed && (
            <button
              type='button'
              className='psr-all'
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => showAll(e.metaKey || e.ctrlKey)}
            >
              <span>
                See all results for “<strong>{term}</strong>”
              </span>
              <FaArrowRight aria-hidden='true' />
            </button>
          )}

          <div className='psr-pop__foot'>
            {showingRecent ? (
              <span>
                <kbd>↑</kbd>
                <kbd>↓</kbd> then <kbd>Enter</kbd> to open
              </span>
            ) : (
              <>
                <span>
                  <kbd>Enter</kbd> all results
                </span>
                <span>
                  <kbd>↑</kbd>
                  <kbd>↓</kbd> then <kbd>Enter</kbd> to open one
                </span>
              </>
            )}
            <span>
              <kbd>{isMac ? '⌘' : 'Ctrl'}</kbd>+<kbd>Enter</kbd> new tab
            </span>
            {!showingRecent && total > 0 && (
              <span className='psr-pop__count'>
                {more.length > 0 ? `Newest ${total} shown` : `${total} found`}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default AppSearch
